import * as Sentry from '@sentry/react';
import { getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

import { propTypesDataBenchmarkGrid } from './BenchmarkGrid.proptypes';

import Picture from '../../../../ui/Atoms/Picture';
import Text, { TextAppearance } from '../../../../ui/Atoms/Text';

import { getStaticUrl } from '../../../utils/getStaticUrl';

import * as S from './BenchmarkGrid.styles';

const renderStars = stars => {
  const numStars = parseInt(stars, 10);
  if (isNaN(numStars)) {
    return null;
  }

  return (
    <S.StarWrapper>
      {Array.from({ length: numStars }, (_, index) => (
        <S.Star key={index} role='img' aria-label='star'>
          ⭐️
        </S.Star>
      ))}
    </S.StarWrapper>
  );
};

const renderCategoryRow = (category, items, key) => {
  if (!category || !key) {
    return null;
  }

  return (
    <>
      <S.StyledRow>
        <S.StyledSuperTitle colSpan={`${items?.length}`}>
          <Text>{category}</Text>
        </S.StyledSuperTitle>
      </S.StyledRow>
      <S.StyledRow>
        {items.map((item, index) => {
          const content = item[`${key}_content`]?.html || '';
          const rate = item[`${key}_rate`] || '';
          const isMaximum = rate === '5';

          return (
            <S.StyledContent isMaximum={isMaximum} key={index}>
              <S.TextWrapper>
                {isMaximum && <>🏆</>}
                {renderStars(rate)}
                {content && (
                  <S.Description>
                    <Text type={TextAppearance.INFO}>{content}</Text>
                  </S.Description>
                )}
              </S.TextWrapper>
            </S.StyledContent>
          );
        })}
      </S.StyledRow>
    </>
  );
};

export const BenchmarkGridSlice = ({ slice }) => {
  if (!slice) {
    return null;
  }

  const data = slice.primary || {};
  const {
    benchmark_grid_slice_id: id,
    category_one: categoryOne,
    category_two: categoryTwo,
    category_three: categoryThree,
    category_four: categoryFour,
    category_five: categoryFive,
    category_six: categorySix,
    category_seven: categorySeven,
    category_height: categoryHeight,
    category_nine: categoryNine,
  } = data || '';

  const items = slice.items || [];
  if (items?.length < 2) {
    Sentry.captureMessage(
      'Error: missing required data in BenchMarkGrid slice'
    );
    return null;
  }

  return (
    <S.Wrapper id={id}>
      <S.StyledHead>
        <S.StyledRow>
          {items.map((item, index) => {
            const title = item.column_title || '';

            return <S.StyledTitle key={index}>{title}</S.StyledTitle>;
          })}
        </S.StyledRow>
      </S.StyledHead>
      <S.StyledRow>
        {items.map((item, index) => {
          const { alt, localFile } = item.column_logo || '';

          const imageProps = {
            imageObject: getImage(localFile),
            imageUrl: getStaticUrl(localFile?.publicURL),
            altText: alt,
            isNotLazy: false,
          };

          return (
            <S.StyledContent key={index}>
              <S.ImageWrapper>
                <Picture {...imageProps} />
              </S.ImageWrapper>
            </S.StyledContent>
          );
        })}
      </S.StyledRow>

      {renderCategoryRow(categoryOne, items, 'category_one')}
      {renderCategoryRow(categoryTwo, items, 'category_two')}
      {renderCategoryRow(categoryThree, items, 'category_three')}
      {renderCategoryRow(categoryFour, items, 'category_four')}
      {renderCategoryRow(categoryFive, items, 'category_five')}
      {renderCategoryRow(categorySix, items, 'category_six')}
      {renderCategoryRow(categorySeven, items, 'category_seven')}
      {renderCategoryRow(categoryHeight, items, 'category_seven')}
      {renderCategoryRow(categoryNine, items, 'category_seven')}

      <S.StyledRow>
        {items.map((item, index) => {
          const cta = item.cta?.html || '';

          return (
            <S.StyledCta key={index}>
              <Text>{cta}</Text>
            </S.StyledCta>
          );
        })}
      </S.StyledRow>
    </S.Wrapper>
  );
};

BenchmarkGridSlice.propTypes = {
  /**
   * Content of the BenchmarkGrid slice.
   */
  slice: PropTypes.shape(propTypesDataBenchmarkGrid),
};
